import type maplibregl from 'maplibre-gl';

import { ALTITUDE_EXAGGERATION } from './aircraftSource';
import { TOPOGRAPHY_LAYER_ID, mapBridge } from './mapBridge';

/** Optional Pages build-time key. Never required — unkeyed CARTO raster is watermarked. */
const CARTO_KEY = (import.meta.env.VITE_CARTO_API_KEY ?? '').trim();

/** Keyless dark vector style (OpenFreeMap / OpenMapTiles). No API key. */
export const OPENFREEMAP_DARK_STYLE = 'https://tiles.openfreemap.org/styles/dark';

/** Mapzen/AWS terrarium DEM — same source production already uses. */
const TERRAIN_URL = (import.meta.env.VITE_TERRAIN_URL ?? '').trim();

export const TERRAIN_DEM: maplibregl.RasterDEMSourceSpecification = TERRAIN_URL
  ? { type: 'raster-dem', url: TERRAIN_URL, encoding: 'terrarium' }
  : {
      type: 'raster-dem',
      tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
      encoding: 'terrarium',
      tileSize: 256,
      maxzoom: 15,
      attribution: 'Terrain © Mapzen / AWS elevation-tiles-prod',
    };

export const MAP_SKY: maplibregl.SkySpecification = {
  'sky-color': '#020617',
  'horizon-color': '#1e3a5f',
  'fog-color': '#0b1020',
  'sky-horizon-blend': 0.15,
  'horizon-fog-blend': 0.35,
  'fog-ground-blend': 0.88,
  'atmosphere-blend': 1,
};

const HILLSHADE_PAINT: maplibregl.HillshadeLayerSpecification['paint'] = {
  'hillshade-shadow-color': '#020617',
  'hillshade-exaggeration': 0.6,
};

/** Fonts present on OpenFreeMap; include MapLibre demo fallbacks for keyed CARTO raster. */
export const MAP_TEXT_FONTS = ['Noto Sans Regular', 'Open Sans Regular', 'Arial Unicode MS Regular'];

function cartoRasterTiles(key: string): string[] {
  const q = `?key=${encodeURIComponent(key)}`;
  return ['a', 'b', 'c'].map(
    (s) => `https://${s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png${q}`,
  );
}

function keyedCartoStyle(key: string): maplibregl.StyleSpecification {
  return {
    version: 8,
    glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
    sources: {
      carto: {
        type: 'raster',
        tiles: cartoRasterTiles(key),
        tileSize: 256,
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
      },
      terrainSource: TERRAIN_DEM,
      hillshadeSource: TERRAIN_DEM,
    },
    layers: [
      { id: 'carto', type: 'raster', source: 'carto' },
      {
        id: TOPOGRAPHY_LAYER_ID,
        type: 'hillshade',
        source: 'hillshadeSource',
        paint: HILLSHADE_PAINT,
      },
    ],
    terrain: { source: 'terrainSource', exaggeration: ALTITUDE_EXAGGERATION },
    sky: MAP_SKY,
  };
}

/**
 * Default: OpenFreeMap dark (no key). If `VITE_CARTO_API_KEY` is set at Pages build
 * time, keep the original CARTO dark raster with `?key=`.
 */
export function mapStyle(): maplibregl.StyleSpecification | string {
  return CARTO_KEY ? keyedCartoStyle(CARTO_KEY) : OPENFREEMAP_DARK_STYLE;
}

/** Add DEM / hillshade / sky when the basemap style does not already include them. */
export function ensureTerrainAndSky(map: maplibregl.Map): void {
  if (!map.getSource('terrainSource')) {
    map.addSource('terrainSource', TERRAIN_DEM);
  }
  if (!map.getSource('hillshadeSource')) {
    map.addSource('hillshadeSource', TERRAIN_DEM);
  }
  if (!map.getLayer(TOPOGRAPHY_LAYER_ID)) {
    const before = map.getLayer('flight-trail-glow') ? 'flight-trail-glow' : undefined;
    map.addLayer(
      {
        id: TOPOGRAPHY_LAYER_ID,
        type: 'hillshade',
        source: 'hillshadeSource',
        paint: HILLSHADE_PAINT,
      },
      before,
    );
  }
  try {
    map.setTerrain({ source: 'terrainSource', exaggeration: ALTITUDE_EXAGGERATION });
  } catch {
    // Some styles already define terrain.
  }
  try {
    map.setSky(MAP_SKY);
  } catch {
    // Sky not supported.
  }
  mapBridge.syncTopographyLayer();
}
